<template>
  <div v-if="user">
    <div v-if="error != null" class="alert alert-danger" role="alert">
      {{error}}
    </div>

    <div class="card" style="width: 1200px">
      <form id="userForm" @submit.prevent="processForm()" class="m-0 p-0">
        <div class="card-body">
          <div class="form-row">
            <div class="form-group col-md-4">
              <label>First Name</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': fieldErrors('first_name') }"
                v-model="user.first_name"
              />
              <div class="invalid-feedback" v-if="fieldErrors('first_name')">
                {{ fieldErrors('first_name') }}
              </div>
            </div>

            <div class="form-group col-md-4">
              <label>Last Name</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': fieldErrors('last_name') }"
                v-model="user.last_name"
              />
              <div class="invalid-feedback" v-if="fieldErrors('last_name')">
                {{ fieldErrors('last_name') }}
              </div>
            </div>

            <div class="form-group col-md-4">
              <label>Email</label>
              <input
                type="email"
                class="form-control"
                :class="{ 'is-invalid': fieldErrors('email') }"
                v-model="user.email"
              />
              <div class="invalid-feedback" v-if="fieldErrors('email')">
                {{ fieldErrors('email') }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-4">
              <label>User Type</label>
              <select
                class="form-control"
                :class="{ 'is-invalid': fieldErrors('user_type') }"
                v-model="user.user_type"
              >
                <option disabled value="">Select type…</option>
                <option value="admin">admin</option>
                <option value="advisor">advisor</option>
                <option value="developer">developer</option>
                <option value="office">office</option>
                <option value="training">training</option>
              </select>
              <div class="invalid-feedback" v-if="fieldErrors('user_type')">
                {{ fieldErrors('user_type') }}
              </div>
            </div>

            <div class="form-group col-md-4">
              <label>Location</label>
              {{user.location_id}}
              <select
                class="form-control"
                :class="{ 'is-invalid': fieldErrors('location_id') }"
                v-model="user.location_id"
              >
                <option v-for="location in locations" :key="location.id" :value="location.id"> {{location.name}}</option>
              </select>
              <div class="invalid-feedback" v-if="fieldErrors('location_id')">
                {{ fieldErrors('location_id') }}
              </div>
            </div>

            <div class="form-group col-md-4">
              <label>Preferences</label>
              <p>
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="user.enable_uploads"
                  >&nbsp;PhotoBuddy Access
                </p>
              <p v-if="user.user_type ==='advisor'">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="user.send_customer_notifications"
                  >&nbsp;Send Customer Notifications              
              </p>
            </div>
          </div>          
        </div>

        <hr>

        <div class="card mt-3">
          <table class="table is-full-width">
            <thead>
              <tr>
                <th>Service Organization</th>
                <th>Advisor Code</th>
                <th>
                  <button type="button" class="btn btn-sm btn-default" @click="addAdvisor">
                    <i class="fa fa-plus"></i>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(adv, idx) in user.advisors"
                :key="adv._key || adv.id || idx"
                :errors="getNestedErrors(errors, 'advisors', idx)"                
              >
                <td>
                  <select
                    class="form-control"
                    v-model="adv.service_organization_id"
                    :class="{ 'is-invalid': fieldErrors('service_organization_id') }"
                  >
                    <option v-for="org in service_organizations" :key="org.id" :value="org.id">
                      {{ org.name }} ({{ org.code }})
                    </option>
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    class="form-control"
                    v-model="adv.code"
                    :class="{ 'is-invalid': fieldErrors('advisors', idx, 'code') }"
                  />
                </td>
                <td>
                  <button v-if="adv._destroy" type="button" class="btn btn-warning btn-sm" @click="restoreAdvisor(idx)">
                    <i class="fa fa-undo"></i>
                  </button>       
                  <button v-else type="button" class="btn btn-danger btn-sm" @click="removeAdvisor(idx)">
                    <i class="fa fa-minus"></i>
                  </button>                             
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <hr>

        <div class="card-footer d-flex">
          <button class="btn btn-success btn-block mr-2" type="submit">Save</button>

          <a
            v-if="isEdit()"
            href="/users"
            class="btn btn-danger btn-block"
          >
            Cancel
          </a>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import formFieldsMixin from '../mixins/formFieldsMixin'
 
export default {
  name: 'UserForm',
  props: {
    id: {
      type: String,
      default: null
    }
  },
  mixins: [formFieldsMixin],
  data() {
    return {
      user: {
        first_name: null,
        last_name: null,
        email: null,
        location_id: null,
        user_type: 'advisor',
        enable_uploads: false,
        send_customer_notifications: false,
        advisors: []
      },
      errors: {},
      service_organizations: [],
      locations: [],
      error: null
    }
  },
  mounted () {
    this.getServiceOrganizations()
    this.getLocations()

    if(this.isEdit()) {
      this.getUser()
    }
  },
  methods: {
    title() {
      return this.isNew() ? 'New User' : `Edit: ${this.first_name}`
    },    
    formOperation () {
      return this.isNew() ? 'New' : 'Edit'
    },
    isNew() {
      return !this.isEdit()
    },
    isEdit() {
      return this.id !== null
    },
    getUser () {
      var url = `/api/users/${this.id}`

      this.$secured.get(url)
        .then(response => {
          console.log(response.data)
          this.user = response.data.user
        })
        .catch((error) => {
          this.error = error.msg
        }) 
    }, 
    getServiceOrganizations () {
      var url = `/api/service_organizations`

      this.$secured.get(url)
        .then(response => {
          this.service_organizations = response.data.service_organizations
        })
        .catch((error) => {
          this.error = error.msg
        }) 
    },  
    getLocations () {
      var url = `/api/locations`

      this.$secured.get(url)
        .then(response => {
          this.locations = response.data.locations
        })
        .catch((error) => {
          this.error = error.msg
        }) 
    },           
    processForm() {
      var params = {
        user: this.user
      }

      if(this.isEdit()) {
        this.updateUser(params)        
      } else {
        this.addUser(params)
      }
    },   
    addUser(params) {
      this.$secured.post('/api/users', params)
      .then((response) => {
        window.location.href = `/users/${response.data.user.id}`
      })
      .catch((error) => {
        if (error.response) { 
          this.errors = error.response.data.errors
        }
        this.error = `Failed to add user (${error.message}). Please check any errors and resubmit.`
      })   
    },
    updateUser(params) {
      this.$secured.put(`/api/users/${this.user.id}`, params)
      .then((response) => {
        window.location.href = `/users/${response.data.user.id}`
      })
      .catch((error) => {
        if (error.response) {
          this.errors = error.response.data.errors
        }
        this.error = `Failed to update user (${error.message}). Please check any errors and resubmit.`
      })   
    },
    addAdvisor () {
      this.user.advisors.push({
        _key: Math.random().toString(36).slice(2),
        service_organization_id: this.service_organizations[0].id,
        code: ''
      })
    },
    removeAdvisor (idx) {
      const row = this.user.advisors[idx]
      if (row.id) {
        this.$set(this.user.advisors, idx, { ...row, _destroy: true })
      } else {
        this.user.advisors.splice(idx, 1)
      }
    },
    restoreAdvisor (idx) {
      const row = this.user.advisors[idx]
      if (row.id) {
        this.$set(this.user.advisors, idx, { ...row, _destroy: false })
      }
    },    
    visibleAdvisors () {
      return this.user.advisors.filter(a => !a._destroy)
    }  
  }
}
</script>

